import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { PaymentDetails } from '@/lib/api/payments';
import { formatMoney } from '@/lib/money';
import { buildRefundReceiptPdf } from '@/lib/pdf/refund-receipt';
import { useToast } from '@/components/ui/use-toast';
import { useFormatDate } from '@/hooks/use-format-date';

export interface UseDownloadRefundReceiptResult {
  download: () => Promise<void>;
  hasRefund: boolean;
  isGenerating: boolean;
}

export function useDownloadRefundReceipt(
  payment: PaymentDetails | undefined,
): UseDownloadRefundReceiptResult {
  const { t } = useTranslation('payments');
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = React.useState(false);
  const { format: formatTz } = useFormatDate();

  const hasRefund = !!payment && (payment.refundedAmount ?? 0) > 0;

  const download = React.useCallback(async () => {
    if (!payment || !hasRefund) return;

    setIsGenerating(true);
    try {
      const bytes = await buildRefundReceiptPdf({
        payment,
        labels: {
          title: t('refundReceipt.title'),
          customer: t('refundReceipt.customer'),
          customerEmail: t('refundReceipt.customerEmail'),
          orderId: t('refundReceipt.orderId'),
          paymentId: t('refundReceipt.paymentId'),
          originalPaymentDate: t('refundReceipt.originalPaymentDate'),
          originalPaymentAmount: t('refundReceipt.originalPaymentAmount'),
          paymentMethod: t('refundReceipt.paymentMethod'),
          refundDate: t('refundReceipt.refundDate'),
          refundTransactionId: t('refundReceipt.refundTransactionId'),
          amount: t('refundReceipt.amount'),
          totalRefunded: t('refundReceipt.totalRefunded'),
          generatedAt: t('refundReceipt.generatedAt', { date: '{{date}}' }),
          testWatermark: t('refundReceipt.testWatermark'),
          notAvailable: t('notAvailable'),
        },
        formatters: {
          formatMoney,
          formatDate: (date) => formatTz(date, 'MMM dd, yyyy HH:mm'),
        },
      });

      const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `refund-receipt-${payment.id}.pdf`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: t('refundReceipt.failed'),
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [payment, hasRefund, t, toast, formatTz]);

  return { download, hasRefund, isGenerating };
}
